/**
 * Registre de l'aide contextuelle — SOURCE UNIQUE.
 *
 * Trois choses vivent ici, volontairement en données et non en composants,
 * pour qu'un rédacteur puisse enrichir l'aide sans toucher au code des pages :
 *
 *   - les SUJETS (`HELP_TOPICS`) : une bulle = un sujet. Titre, texte court,
 *     étapes numérotées optionnelles, astuce, et l'ancre du guide (`/aide#…`)
 *     qui développe le sujet ;
 *   - les VISITES (`TOURS`) : suites d'étapes qui projettent un sujet sur un
 *     élément de l'écran repéré par son attribut `data-help` ;
 *   - le GUIDE (`guide.ts`) : les sections longues du centre d'aide, dont le
 *     Markdown versionné est généré (`pnpm help:guide`).
 *
 * Une cible absente (module masqué, permission manquante — INV-6) n'est jamais
 * bloquante : l'étape est sautée par le moteur de visite.
 */
import type { MeResponse } from "@/features/auth/types";
import { can } from "@/lib/permissions/can";

import type { SetupProgress } from "./api";

export type HelpSide = "top" | "right" | "bottom" | "left";

export interface HelpTopic {
  /** Identifiant stable `domaine.sujet` — clé de mémorisation « Compris ». */
  id: string;
  title: string;
  /** Une à trois phrases. Pas de jargon : le lecteur n'est pas informaticien. */
  body: string;
  /** Marche à suivre, une action par ligne. */
  steps?: string[];
  /** Conseil de terrain, affiché en encadré. */
  tip?: string;
  /** Section du guide (`/aide#<guide>`) qui développe le sujet. */
  guide?: string;
}

export interface TourStep {
  /** Valeur de l'attribut `data-help` de l'élément visé. */
  target: string;
  /** Sujet affiché dans la carte de l'étape. */
  topic: string;
  /** Côté préféré de la carte par rapport à la cible. */
  side?: HelpSide;
  /** Page où vit la cible : la visite y navigue si besoin. */
  route?: string;
  /** Permission nécessaire pour voir la cible (étape sautée sinon). */
  permission?: string;
}

export interface Tour {
  id: string;
  title: string;
  description: string;
  /** Durée annoncée à l'utilisateur. */
  minutes: number;
  /** Page de départ. */
  route: string;
  steps: TourStep[];
  /** Permission requise pour proposer la visite (aucune = tout le monde). */
  permission?: string;
  /** Réservée à l'administration du compte (super admin, éditeur GTS). */
  adminOnly?: boolean;
}

const T = (topic: HelpTopic): HelpTopic => topic;

export const HELP_TOPICS: Record<string, HelpTopic> = {
  // ---- Inscription -------------------------------------------------------
  "signup.name": T({
    id: "signup.name",
    title: "Nom de la clinique",
    body: "Le nom tel qu'il apparaîtra sur vos factures, ordonnances et SMS. Vous pourrez le corriger plus tard dans les paramètres.",
    tip: "Écrivez le nom complet : « Clinique Espoir Kaloum » plutôt que « Espoir ».",
    guide: "creer-son-espace",
  }),
  "signup.slug": T({
    id: "signup.slug",
    title: "Adresse de votre espace",
    body: "C'est l'adresse web que votre équipe tapera pour se connecter. Lettres minuscules, chiffres et tirets uniquement.",
    steps: [
      "Choisissez un mot court et reconnaissable.",
      "Vérifiez l'aperçu vert sous le champ.",
      "Notez cette adresse : elle ne change pas.",
    ],
    guide: "creer-son-espace",
  }),
  "signup.email": T({
    id: "signup.email",
    title: "E-mail de l'administrateur",
    body: "Cette adresse devient le premier compte de l'espace, avec tous les droits. Elle reçoit le lien qui vous fait définir votre mot de passe : aucun mot de passe n'est demandé ici.",
    tip: "Utilisez une adresse que vous consultez vraiment, pas une boîte partagée.",
    guide: "creer-son-espace",
  }),
  "signup.consent": T({
    id: "signup.consent",
    title: "Politique de confidentialité",
    body: "Vos données et celles de vos patients sont stockées dans une base dédiée à votre clinique. La politique décrit ce que MADA fait et ne fait pas avec elles.",
    guide: "creer-son-espace",
  }),
  "signup.provisioning": T({
    id: "signup.provisioning",
    title: "Préparation de votre espace",
    body: "MADA crée votre base de données, votre clinique, les rôles et votre compte administrateur. Cela prend environ une minute.",
    steps: [
      "Laissez la page ouverte.",
      "Quand le bouton vert apparaît, cliquez pour entrer.",
      "Vous êtes connecté automatiquement la première fois.",
    ],
    guide: "creer-son-espace",
  }),

  // ---- Barre de navigation ----------------------------------------------
  "nav.menu": T({
    id: "nav.menu",
    title: "Les menus",
    body: "Toute l'application tient dans cette barre. Chaque menu regroupe un métier : Clinique pour les patients et les soins, Gestion pour l'argent, Administration pour l'équipe.",
    tip: "Survolez un menu pour l'ouvrir. Sur téléphone, touchez les trois traits en haut à gauche.",
    guide: "se-reperer",
  }),
  "nav.search": T({
    id: "nav.search",
    title: "Recherche et actions rapides",
    body: "Tapez un nom de patient, un numéro de fiche ou une action (« nouvelle facture ») et MADA vous y emmène.",
    tip: "Raccourci clavier : Ctrl + K (Cmd + K sur Mac).",
    guide: "se-reperer",
  }),
  "nav.bell": T({
    id: "nav.bell",
    title: "Vos notifications",
    body: "La cloche signale ce qui demande votre attention : rendez-vous imminents, stock bas, factures en retard. Le chiffre rouge compte les non lues.",
    steps: [
      "Cliquez sur la cloche pour voir les dernières.",
      "Cliquez sur une notification pour ouvrir la fiche concernée.",
      "« Tout marquer lu » vide le compteur.",
    ],
    guide: "notifications",
  }),
  "nav.clinic": T({
    id: "nav.clinic",
    title: "Clinique active",
    body: "Si votre compte couvre plusieurs agences, c'est ici que vous changez de clinique. Tout ce que vous voyez appartient à la clinique affichée.",
    guide: "se-reperer",
  }),
  "nav.amounts": T({
    id: "nav.amounts",
    title: "Masquer les montants",
    body: "Cache tous les montants à l'écran, utile quand un patient regarde par-dessus votre épaule. Les chiffres reviennent d'un clic.",
    guide: "se-reperer",
  }),
  "nav.user": T({
    id: "nav.user",
    title: "Votre compte",
    body: "Votre profil, votre rôle dans la clinique, vos préférences de notifications et la déconnexion.",
    guide: "se-reperer",
  }),
  "nav.help": T({
    id: "nav.help",
    title: "Le menu Aide",
    body: "Revenez ici à tout moment : guide complet, visites guidées écran par écran, et le contact GoldenTech si vous êtes bloqué.",
    guide: "se-reperer",
  }),

  // ---- Tableau de bord ---------------------------------------------------
  "dashboard.header": T({
    id: "dashboard.header",
    title: "Votre tableau de bord",
    body: "La photo du jour de votre clinique : ce qu'il reste à faire, les chiffres clés et vos raccourcis. Chaque bloc se cache ou se réordonne dans les paramètres d'affichage.",
    guide: "tableau-de-bord",
  }),
  "dashboard.quick": T({
    id: "dashboard.quick",
    title: "Raccourcis",
    body: "Les gestes les plus fréquents de votre rôle, à un clic : nouveau patient, nouvelle consultation, nouvelle facture.",
    guide: "tableau-de-bord",
  }),
  "dashboard.checklist": T({
    id: "dashboard.checklist",
    title: "Premiers pas",
    body: "Votre liste de mise en route. Chaque ligne ouvre l'écran concerné et sa visite guidée. Cochez au fur et à mesure : la carte disparaît quand tout est fait.",
    guide: "premiers-pas",
  }),

  // ---- Patients ----------------------------------------------------------
  "patients.list": T({
    id: "patients.list",
    title: "La liste des patients",
    body: "Toutes les fiches de la clinique, avec recherche instantanée par nom, téléphone ou numéro de fiche. Cliquez sur une ligne pour ouvrir le dossier.",
    guide: "patients",
  }),
  "patients.new": T({
    id: "patients.new",
    title: "Créer une fiche patient",
    body: "Nom, prénom, téléphone et date de naissance suffisent. Le numéro de fiche est attribué automatiquement, par clinique.",
    steps: [
      "Cliquez sur « Nouveau patient ».",
      "Renseignez au moins le nom et le téléphone.",
      "Enregistrez : la fiche s'ouvre, prête pour une consultation.",
    ],
    tip: "Le téléphone sert aux rappels SMS et regroupe les membres d'une même famille.",
    guide: "patients",
  }),
  "patients.search": T({
    id: "patients.search",
    title: "Rechercher un patient",
    body: "La recherche ignore les accents et les majuscules. « diallo » trouve « DIALLO » et « Diallo ».",
    guide: "patients",
  }),
  "patients.families": T({
    id: "patients.families",
    title: "Familles",
    body: "Les fiches partageant un même numéro de téléphone forment un foyer. Pratique pour retrouver les enfants d'un parent.",
    guide: "patients",
  }),
  "patients.trash": T({
    id: "patients.trash",
    title: "Corbeille",
    body: "Une fiche supprimée n'est jamais effacée tout de suite : elle attend ici et se restaure d'un clic.",
    guide: "patients",
  }),

  // ---- Consultations -----------------------------------------------------
  "consultations.list": T({
    id: "consultations.list",
    title: "Les consultations",
    body: "Chaque passage d'un patient devant un praticien. De la consultation naissent l'ordonnance, l'examen et la facture.",
    guide: "consultations",
  }),
  "consultations.new": T({
    id: "consultations.new",
    title: "Nouvelle consultation",
    body: "Choisissez le patient, le praticien et le motif. Les onglets de la consultation guident ensuite l'acte : constantes, examen, diagnostic, ordonnance.",
    steps: [
      "Cliquez sur « Nouvelle consultation ».",
      "Tapez les premières lettres du nom du patient.",
      "Enregistrez, puis remplissez les onglets dans l'ordre.",
    ],
    guide: "consultations",
  }),

  // ---- Factures et argent -----------------------------------------------
  "sales.list": T({
    id: "sales.list",
    title: "Les factures",
    body: "Tout ce qui a été vendu ou facturé. Les quatre chiffres du haut résument le facturé, l'encaissé, l'impayé patient et ce que doivent les assureurs.",
    guide: "factures-et-caisse",
  }),
  "sales.new": T({
    id: "sales.new",
    title: "Nouvelle facture",
    body: "Un devis (proforma) peut précéder la facture. Ajoutez les lignes, la part assurance éventuelle, puis encaissez.",
    steps: [
      "Cliquez sur « Nouvelle facture ».",
      "Choisissez le patient ou le client.",
      "Ajoutez les produits ou prestations.",
      "Encaissez tout ou partie : le reste devient un impayé suivi.",
    ],
    tip: "Une facture avec assurance sépare la part patient et la part assureur automatiquement.",
    guide: "factures-et-caisse",
  }),
  "cash.closure": T({
    id: "cash.closure",
    title: "Clôture de caisse",
    body: "En fin de journée, comparez ce que MADA a encaissé et ce qu'il y a dans le tiroir. L'écart est noté et tracé.",
    guide: "factures-et-caisse",
  }),

  // ---- Notifications -----------------------------------------------------
  "notifications.page": T({
    id: "notifications.page",
    title: "Historique des notifications",
    body: "Toutes vos notifications, les plus récentes en premier. Une ligne surlignée n'a pas encore été lue.",
    guide: "notifications",
  }),
  "notifications.legend": T({
    id: "notifications.legend",
    title: "Canaux et états",
    body: "Une notification interne s'affiche dans la cloche. Un e-mail ou un SMS part vers le patient ou vers vous. L'état dit où elle en est.",
    steps: [
      "En attente : programmée, pas encore partie.",
      "Envoyée : remise au destinataire.",
      "Échouée : le réseau ou l'opérateur a refusé, à réessayer.",
      "Lue : vous l'avez ouverte.",
    ],
    guide: "notifications",
  }),
  "notifications.markAll": T({
    id: "notifications.markAll",
    title: "Tout marquer lu",
    body: "Remet le compteur de la cloche à zéro sans rien supprimer : l'historique reste consultable.",
    guide: "notifications",
  }),
  "notifications.prefs": T({
    id: "notifications.prefs",
    title: "Choisir vos notifications",
    body: "Chaque catégorie s'active ou se coupe pour vous seul. Une secrétaire garde les rendez-vous, un gérant garde le stock et les factures.",
    tip: "Coupée, une catégorie n'est plus émise du tout : rien à rattraper plus tard.",
    guide: "notifications",
  }),

  // ---- Équipe et paramètres ---------------------------------------------
  "users.list": T({
    id: "users.list",
    title: "Votre équipe",
    body: "Chaque personne qui se connecte a son propre compte et son rôle. Ne partagez jamais un compte : l'historique dit qui a fait quoi.",
    guide: "equipe-et-roles",
  }),
  "users.new": T({
    id: "users.new",
    title: "Inviter un collaborateur",
    body: "Créez le compte avec son e-mail et son rôle. Un mot de passe temporaire lui est remis : il devra le changer à sa première connexion.",
    steps: [
      "Cliquez sur « Nouvel utilisateur ».",
      "Choisissez le rôle : secrétaire, praticien, caissier, gérant…",
      "Transmettez le mot de passe temporaire de vive voix.",
    ],
    guide: "equipe-et-roles",
  }),
  "settings.hub": T({
    id: "settings.hub",
    title: "Les paramètres",
    body: "Tout ce qui se règle dans MADA est ici, dans une seule page recherchable : identité de la clinique, agences, modules visibles, SMS, assurances, licence.",
    steps: [
      "Tapez un mot dans la recherche (« logo », « SMS », « TVA »).",
      "Ouvrez la section, modifiez, enregistrez.",
    ],
    guide: "parametrer-sa-clinique",
  }),
  "sms.credit": T({
    id: "sms.credit",
    title: "Crédit SMS",
    body: "Les rappels de rendez-vous et les campagnes consomment un crédit prépayé. Rechargez par pack, payez par mobile money, le solde se met à jour aussitôt.",
    guide: "sms",
  }),
  "profile.card": T({
    id: "profile.card",
    title: "Votre profil",
    body: "Vos informations, votre mot de passe et vos préférences de notifications. Le rôle affiché est celui que vous portez dans la clinique active.",
    guide: "se-reperer",
  }),
};

export const TOURS: Tour[] = [
  {
    id: "decouverte",
    title: "Découvrir MADA",
    description: "La barre de navigation, la recherche, la cloche et votre tableau de bord.",
    minutes: 2,
    route: "/dashboard",
    steps: [
      { target: "nav.menu", topic: "nav.menu", side: "bottom" },
      { target: "nav.search", topic: "nav.search", side: "bottom" },
      { target: "nav.bell", topic: "nav.bell", side: "bottom" },
      { target: "nav.clinic", topic: "nav.clinic", side: "bottom" },
      { target: "nav.user", topic: "nav.user", side: "bottom" },
      { target: "nav.help", topic: "nav.help", side: "bottom" },
      { target: "dashboard.header", topic: "dashboard.header", side: "bottom" },
      { target: "dashboard.quick", topic: "dashboard.quick", side: "bottom" },
      { target: "dashboard.checklist", topic: "dashboard.checklist", side: "top" },
    ],
  },
  {
    id: "premier-patient",
    title: "Créer votre premier patient",
    description: "La liste, la recherche et la fiche patient.",
    minutes: 2,
    route: "/patients",
    permission: "patients.view",
    steps: [
      { target: "patients.list", topic: "patients.list", side: "bottom" },
      { target: "patients.search", topic: "patients.search", side: "bottom" },
      { target: "patients.new", topic: "patients.new", side: "bottom", permission: "patients.create" },
      { target: "patients.families", topic: "patients.families", side: "bottom" },
      { target: "patients.trash", topic: "patients.trash", side: "bottom" },
    ],
  },
  {
    id: "premiere-consultation",
    title: "Enregistrer une consultation",
    description: "Du patient à l'ordonnance.",
    minutes: 2,
    route: "/consultations",
    permission: "consultations.view",
    steps: [
      { target: "consultations.list", topic: "consultations.list", side: "bottom" },
      { target: "consultations.new", topic: "consultations.new", side: "bottom", permission: "consultations.create" },
    ],
  },
  {
    id: "premiere-vente",
    title: "Facturer et encaisser",
    description: "Factures, devis, assurance et clôture de caisse.",
    minutes: 3,
    route: "/factures",
    permission: "sales.view",
    steps: [
      { target: "sales.list", topic: "sales.list", side: "bottom" },
      { target: "sales.new", topic: "sales.new", side: "bottom", permission: "sales.create" },
    ],
  },
  {
    id: "notifications",
    title: "Maîtriser les notifications",
    description: "La cloche, l'historique, les états et vos préférences.",
    minutes: 2,
    route: "/notifications",
    steps: [
      { target: "nav.bell", topic: "nav.bell", side: "bottom" },
      { target: "notifications.page", topic: "notifications.page", side: "bottom" },
      { target: "notifications.legend", topic: "notifications.legend", side: "bottom" },
      { target: "notifications.markAll", topic: "notifications.markAll", side: "bottom" },
      { target: "notifications.prefs", topic: "notifications.prefs", side: "top", route: "/profil" },
    ],
  },
  {
    id: "equipe",
    title: "Constituer votre équipe",
    description: "Comptes, rôles et mots de passe temporaires.",
    minutes: 2,
    route: "/admin/utilisateurs",
    permission: "rbac.role.assign",
    adminOnly: true,
    steps: [
      { target: "users.list", topic: "users.list", side: "bottom" },
      { target: "users.new", topic: "users.new", side: "bottom", permission: "rbac.role.assign" },
    ],
  },
  {
    id: "parametrage",
    title: "Paramétrer la clinique",
    description: "Identité, agences, modules, SMS, assurances.",
    minutes: 3,
    route: "/configuration",
    adminOnly: true,
    steps: [{ target: "settings.hub", topic: "settings.hub", side: "bottom" }],
  },
];

/** Étapes de la liste « Premiers pas » du tableau de bord. */
export interface ChecklistItem {
  id: string;
  label: string;
  hint: string;
  to: string;
  /** Visite lancée à l'ouverture (optionnelle). */
  tour?: string;
  permission?: string;
  adminOnly?: boolean;
  /** État calculé sur les données réelles : l'étape se coche d'elle-même
   *  quand la condition est vraie, pas à la fin d'une visite guidée. */
  doneWhen?: (me: MeResponse | null, progress: SetupProgress | undefined) => boolean;
  /** Étape facultative (ex. pas d'assureur) : on peut aussi la passer à la main. */
  canSkip?: boolean;
}

/**
 * Ordre d'une mise en route réussie : sécuriser, paramétrer ce qui s'imprime
 * sur les documents, saisir les référentiels (prix, assureurs), constituer
 * l'équipe, puis dérouler une première journée complète jusqu'à la caisse.
 * Une première facture émise avec un en-tête vide ou sans prix ni assureur
 * coûte la confiance de la clinique : ces étapes viennent avant.
 */
export const CHECKLIST: ChecklistItem[] = [
  {
    id: "discover",
    label: "Découvrir l'application",
    hint: "Deux minutes pour savoir où tout se trouve.",
    to: "/dashboard",
    tour: "decouverte",
  },
  {
    id: "security",
    label: "Sécuriser votre compte",
    hint: "Activez la double authentification : vos dossiers restent protégés même si un mot de passe fuit.",
    to: "/profil",
    doneWhen: (me) => me?.mfa_enabled === true,
  },
  {
    id: "settings",
    label: "Renseigner votre clinique",
    hint: "Logo, adresse, téléphone : ils s'impriment sur vos documents.",
    to: "/configuration",
    tour: "parametrage",
    adminOnly: true,
    doneWhen: (me, p) => p?.profile === true || me?.active_clinic_profile_complete === true,
  },
  {
    id: "catalogue",
    label: "Saisir vos produits et prestations",
    hint: "Montures, verres, actes : leurs prix alimentent devis et factures.",
    to: "/catalogue",
    permission: "inventory.view",
    adminOnly: true,
    doneWhen: (_me, p) => p?.catalogue === true,
  },
  {
    id: "insurers",
    label: "Enregistrer vos assureurs",
    hint: "Leurs taux calculent la part assurance. Aucun assureur ? Passez l'étape.",
    to: "/assurances",
    permission: "insurances.view",
    adminOnly: true,
    canSkip: true,
    doneWhen: (_me, p) => p?.insurers === true,
  },
  {
    id: "team",
    label: "Inviter votre équipe",
    hint: "Un compte par personne, avec le bon rôle.",
    to: "/admin/utilisateurs",
    tour: "equipe",
    permission: "rbac.role.assign",
    adminOnly: true,
    doneWhen: (_me, p) => p?.team === true,
  },
  {
    id: "patient",
    label: "Créer votre premier patient",
    hint: "Nom et téléphone suffisent.",
    to: "/patients",
    tour: "premier-patient",
    permission: "patients.view",
    doneWhen: (_me, p) => p?.patient === true,
  },
  {
    id: "consultation",
    label: "Enregistrer une consultation",
    hint: "Le cœur du dossier médical.",
    to: "/consultations",
    tour: "premiere-consultation",
    permission: "consultations.view",
    doneWhen: (_me, p) => p?.consultation === true,
  },
  {
    id: "sale",
    label: "Émettre votre première facture",
    hint: "Facturer, encaisser, suivre les impayés.",
    to: "/factures",
    tour: "premiere-vente",
    permission: "sales.view",
    doneWhen: (_me, p) => p?.sale === true,
  },
  {
    id: "cash_closure",
    label: "Clôturer votre première caisse",
    hint: "Le soir, comparez l'argent compté à celui enregistré.",
    to: "/caisse/cloture",
    permission: "cash_closure.view",
    doneWhen: (_me, p) => p?.cash_closure === true,
  },
  {
    id: "notifications",
    label: "Régler vos notifications",
    hint: "Recevez seulement ce qui vous concerne.",
    to: "/notifications",
    tour: "notifications",
  },
];

export function getTopic(id: string): HelpTopic | undefined {
  return HELP_TOPICS[id];
}

export function getTour(id: string): Tour | undefined {
  return TOURS.find((t) => t.id === id);
}

/** Administration du compte : super admin de l'abonné ou éditeur GTS. */
export function isAccountAdmin(me: MeResponse | null): boolean {
  if (!me) return false;
  if (me.is_superuser || me.is_platform_staff) return true;
  return (me.active_roles ?? []).some((r) => r.code === "super_admin");
}

/** Visites proposables à l'utilisateur courant (permissions + admin). */
export function toursFor(me: MeResponse | null): Tour[] {
  return TOURS.filter(
    (t) => (!t.permission || can(me, t.permission)) && (!t.adminOnly || isAccountAdmin(me)),
  );
}

/** Étapes d'une visite réellement visibles pour l'utilisateur (INV-6). */
export function stepsFor(tour: Tour, me: MeResponse | null): TourStep[] {
  return tour.steps.filter((s) => !s.permission || can(me, s.permission));
}

/** Une étape est faite si sa condition métier l'est, sinon si elle est cochée. */
export function isChecklistItemDone(
  item: ChecklistItem,
  me: MeResponse | null,
  checklist: Record<string, boolean | undefined>,
  progress?: SetupProgress,
): boolean {
  if (item.doneWhen) {
    if (item.doneWhen(me, progress)) return true;
    // Étape calculée : elle ne se coche à la main que si elle est facultative.
    return item.canSkip === true && !!checklist[item.id];
  }
  return !!checklist[item.id];
}

/** L'étape se coche (ou se passe) à la main ; sinon, la case mène à son écran. */
export function isChecklistItemManual(item: ChecklistItem): boolean {
  return !item.doneWhen || item.canSkip === true;
}

export function checklistFor(me: MeResponse | null): ChecklistItem[] {
  return CHECKLIST.filter(
    (c) => (!c.permission || can(me, c.permission)) && (!c.adminOnly || isAccountAdmin(me)),
  );
}
